/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect } from 'react';
import Select, { OptionsOrGroups, StylesConfig, GroupBase } from 'react-select';
import DataModel from 'model/DataModel';
import Monster from 'model/monster/Monster';
import { buildMonsterSelectorOptions, MonsterOption } from './monsterSelectorOptions';

interface MonsterSelectorProps {
  onMonsterChange: (monsterId: string | null) => void;
  selectedMonsterId: string | null;
  dataModel: DataModel;
  myMonsters: Monster[];
  communityMonsters: { username: string; monster: Monster }[];
}

const MonsterSelector: React.FC<MonsterSelectorProps> = ({ onMonsterChange, selectedMonsterId, dataModel, myMonsters, communityMonsters }) => {
  const [options, setOptions] = useState<OptionsOrGroups<MonsterOption, GroupBase<MonsterOption>>>([]);
  const [selectedMonster, setSelectedMonster] = useState<MonsterOption | null>(null);
  const [loadProgress, setLoadProgress] = useState<string>('Loading Monster Names...');
  const [, setForceUpdate] = useState(0); // Dummy state to force re-renders

  useEffect(() => {
    // This update just sets a member variable to force a redraw:
    const update = () => {
      setForceUpdate((prev) => prev + 1);
      onUpdateDataModel();
    }
    onUpdateDataModel();

    // Cleanup on unmount
    return dataModel.subscribe(update);
  }, [dataModel, dataModel.monsters, dataModel.monsters.progress,
                 dataModel.creatures, dataModel.creatures.progress,
      selectedMonsterId, myMonsters, communityMonsters]);

  const onUpdateDataModel = (() => {
    setLoadProgress(dataModel?.monsters?.progress || dataModel?.creatures?.progress);
    const newOptions: OptionsOrGroups<MonsterOption, GroupBase<MonsterOption>> =
      buildMonsterSelectorOptions(dataModel.monsters.data, dataModel.creatures.data, dataModel.documents,
                                  myMonsters, communityMonsters);
    const selectedOption = newOptions.flatMap(groupOrOption =>
                                                'options' in groupOrOption
                                                  ? groupOrOption.options // Extract options if it's a group
                                                  : [groupOrOption]       // Treat it as a single option
                                              )
                                     .find(option => option.value === selectedMonsterId) || null;
    setSelectedMonster(selectedOption);
    setOptions(newOptions);
  });

  const customStyles: StylesConfig<MonsterOption, false, GroupBase<MonsterOption>> = {
    groupHeading: (base) => ({
      ...base,
      backgroundColor: '#f2f2f2',
      color: '#333',
      paddingLeft: '10px',
      padding: '5px',
      fontSize: '26px',
      fontWeight: 'bold',
      textTransform: 'none',
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      paddingLeft: '40px',
      paddingTop: '0px',
      paddingBottom: '0px',
      backgroundColor: isFocused ? 'lightgray' : 'white',
      color: 'black',
      fontWeight: isSelected ? 'bold' : 'normal',
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    control: (provided) => ({ ...provided, width: '60%', minWidth: '300px' }),
  };

  return (
    <Select
      menuPortalTarget={window.document.body}
      styles={customStyles}
      options={options}
      value={selectedMonster}
      onChange={(selectedOption) => {
        setSelectedMonster(selectedOption); // Update local state
        onMonsterChange(selectedOption ? selectedOption.value : null); // Notify parent component
      }}
      filterOption={(option, inputValue) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase())
      }
      id='monster-selector'
      placeholder={loadProgress || 'Select a monster'}
      isClearable
      isSearchable
    />
  );
};

export default MonsterSelector;
